import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GithubSymbolListsComponent } from './github-symbol-lists/github-symbol-lists.component';
import { SymbolListsRegistryComponent } from './symbol-lists-registry/symbol-lists-registry.component';
import { InstrumentDetailsComponent } from './instrument-details/instrument-details.component';
import { TickersMasterComponent } from './tickers-master/tickers-master.component';
import { MongoExplorerComponent } from './mongo-explorer/mongo-explorer.component';
import { IndexConstituentsComponent } from './index-constituents/index-constituents.component';

// /admin/common-data — hub panel that groups every "common data source"
// the fleet consumes. Left rail = source picker; right pane swaps.
// Started 2026-07-16 as the graduation of /admin/common-symbol-lists.
//
// Adding a new source: extend the `sources` array + import the pane
// component + add its <ng-container *ngIf> branch in the template.
type SourceKey =
  | 'github-symbol-lists'
  | 'symbol-lists-registry'
  | 'instrument-details'
  | 'index-constituents'
  | 'tickers-master'
  | 'mongo-v2'
  | 'mongo-v1';

interface SourceDef {
  key: SourceKey;
  label: string;
  hint: string;
  icon: string;
}

@Component({
  selector: 'app-common-data',
  standalone: true,
  imports: [CommonModule, GithubSymbolListsComponent, SymbolListsRegistryComponent, InstrumentDetailsComponent, TickersMasterComponent, MongoExplorerComponent, IndexConstituentsComponent],
  templateUrl: './common-data.component.html',
  // Host must be a display:block, full-height element for the nested
  // flex-1 + min-h-0 chain inside the template to reach the viewport
  // (router-outlet renders us as its sibling; without host height the
  // ag-grid inside instrument-details has no stretchable parent).
  host: { class: 'block h-full' },
})
export class CommonDataComponent {
  readonly sources: SourceDef[] = [
    {
      key: 'github-symbol-lists',
      label: 'GitHub Symbol Lists',
      hint: 'Common-symbol-lists repo (Redis-cached)',
      icon: 'fa-brands fa-github',
    },
    {
      key: 'symbol-lists-registry',
      label: 'Symbol Lists Registry',
      hint: 'F2-ADMIN.SymbolLists — dynamic per-list settings (repo/path/ticker)',
      icon: 'fa-solid fa-list-check',
    },
    {
      key: 'instrument-details',
      label: 'Instrument Details',
      hint: 'LSEG:INSTRUMENT_DETAILS hash (Massive-backed post-cutover)',
      icon: 'fa-solid fa-microscope',
    },
    {
      key: 'index-constituents',
      label: 'Index Constituents',
      hint: 'LSEG:INDEX_CONSTITUENTS:* (SPX, DJI, NDX, IXIC)',
      icon: 'fa-solid fa-layer-group',
    },
    {
      key: 'tickers-master',
      label: 'Tickers Master',
      hint: 'F2-ADMIN.tickers_master (Mongo, dynamic columns)',
      icon: 'fa-solid fa-tag',
    },
    {
      key: 'mongo-v2',
      label: 'MongoDB V2',
      hint: '.133 stack — pick database + collection (editable)',
      icon: 'fa-solid fa-database',
    },
    {
      key: 'mongo-v1',
      label: 'MongoDB V1',
      hint: '.90/.52 stack — pick database + collection (editable)',
      icon: 'fa-solid fa-database',
    },
  ];

  active: SourceKey = 'github-symbol-lists';

  // When a child panel asks the hub to open a registry entry (via the
  // gear icon on the GitHub Symbol Lists rows), we swap the source and
  // pass the name through so the Registry panel preselects on init.
  registryPreselect: string | null = null;

  select(k: SourceKey) {
    this.active = k;
    // Clear stale preselect when the operator navigates via the source
    // picker — only a same-tick emit from a child should stick.
    if (k !== 'symbol-lists-registry') this.registryPreselect = null;
  }

  editRegistry(name: string) {
    this.registryPreselect = name;
    this.active = 'symbol-lists-registry';
  }

  trackByKey(_: number, s: SourceDef) { return s.key; }
}
